import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getCategoriaDetalle, getSubcategorias, getPublicaciones } from '../services/publicacionesService';
import { getInstitution, getInstitutionTailwind } from '../config/institutionConfig';
import PdfCoverThumbnail from '../components/PdfCoverThumbnail';

const CategoryPage = () => {
  const { categoryId } = useParams();
  const [category, setCategory] = useState(null);
  const [subcategorias, setSubcategorias] = useState([]);
  const [publicaciones, setPublicaciones] = useState([]);
  const [tieneSubcategorias, setTieneSubcategorias] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const institution = getInstitution();
  const tw = getInstitutionTailwind();
  const colors = institution.colors;

  useEffect(() => {
    let esCategoriaConHijos = false;
    getCategoriaDetalle(categoryId)
      .then((detalle) => {
        setCategory(detalle);
        esCategoriaConHijos = detalle.tieneSubcategorias;
        setTieneSubcategorias(detalle.tieneSubcategorias);
        if (detalle.tieneSubcategorias) {
          return getSubcategorias(categoryId);
        } else {
          return getPublicaciones(categoryId);
        }
      })
      .then((items) => {
        if (esCategoriaConHijos) setSubcategorias(items);
        else setPublicaciones(items);
      })
      .catch(() => setError('Error al cargar la categoría'))
      .finally(() => setLoading(false));
  }, [categoryId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-400">Cargando...</p>
      </div>
    );
  }

  if (!category || error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-8">
        <div className="bg-white rounded-2xl shadow-sm p-12 text-center max-w-md border border-gray-200">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-50 rounded-full mb-6">
            <svg className="h-8 w-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Categoría no encontrada</h1>
          <p className="text-gray-500 mb-6">La categoría que buscas no está disponible o no existe.</p>
          <Link
            to="/"
            className="inline-block px-6 py-3 text-white rounded-lg transition-all hover:opacity-90 shadow-md"
            style={{ background: `linear-gradient(135deg, ${colors.accentFrom}, ${colors.accentTo})` }}
          >
            Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0" style={{
          background: `linear-gradient(135deg, ${colors.headerGradientStart}, ${colors.headerGradientMid}, ${colors.headerGradientEnd})`
        }} />
        <div className="absolute inset-0 bg-pattern opacity-10" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <div className="flex items-center gap-5">
            <div className="flex-shrink-0">
              <img
                src={`${process.env.PUBLIC_URL}${institution.logo}`}
                alt={`Logo ${institution.shortName}`}
                className="h-14 sm:h-16 drop-shadow-lg"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight drop-shadow-md">
                {category.nombre}
              </h1>
              <p className="mt-1.5 text-white/85 text-sm sm:text-base font-light">
                {category.descripcion}
              </p>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full h-auto block">
            <path d="M0 60V30C240 0 480 0 720 30C960 60 1200 60 1440 30V60H0Z" fill="#F9FAFB" />
          </svg>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Breadcrumb */}
        <nav className="mb-8 flex items-center text-sm flex-wrap gap-y-1">
          <Link to="/" className="font-medium transition-colors hover:opacity-80" style={{ color: colors.ctaText }}>
            Centro de Publicaciones
          </Link>
          {category.ruta?.slice(0, -1).map((item) => (
            <React.Fragment key={item.id}>
              <svg className="h-4 w-4 mx-2 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <Link to={`/${item.slug}`} className="font-medium transition-colors hover:opacity-80" style={{ color: colors.ctaText }}>
                {item.nombre}
              </Link>
            </React.Fragment>
          ))}
          <svg className="h-4 w-4 mx-2 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-gray-500">{category.nombre}</span>
        </nav>

        <div className="mb-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            {tieneSubcategorias ? 'Subcategorías' : 'Publicaciones Disponibles'}
          </h2>
          <p className="text-gray-500 text-base sm:text-lg max-w-2xl mx-auto">
            {tieneSubcategorias
              ? 'Selecciona una subcategoría para explorar sus publicaciones'
              : 'Haz clic en una publicación para visualizarla de manera interactiva'}
          </p>
        </div>

        {/* Render subcategorías o publicaciones según tieneSubcategorias */}
        {tieneSubcategorias ? (
          subcategorias.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm p-12 text-center border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-2">No hay subcategorías disponibles</h3>
              <p className="text-gray-500">Aún no hay subcategorías en esta categoría</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {subcategorias.map((subcat) => (
                <Link
                  key={subcat.id}
                  to={`/${subcat.slug}`}
                  className="group relative bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-gray-300 hover:-translate-y-1"
                >
                  <div
                    className="relative h-36 overflow-hidden"
                    style={{
                      background: `linear-gradient(135deg, ${colors.cardGradientFrom}, ${colors.cardGradientVia}, ${colors.cardGradientTo})`
                    }}
                  >
                    <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/10" />
                    <div className="absolute -bottom-4 -left-4 w-20 h-20 rounded-full bg-white/10" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="flex items-center justify-center w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl group-hover:scale-110 transition-transform duration-300">
                        <svg className="w-7 h-7 text-white flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{subcat.nombre}</h3>
                    <p className="text-sm text-gray-500 mb-4 line-clamp-2 leading-relaxed">{subcat.descripcion}</p>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <span className="font-semibold text-sm transition-colors" style={{ color: colors.ctaText }}>
                        Ver publicaciones
                      </span>
                      <span
                        className="flex items-center justify-center w-8 h-8 rounded-full transition-all duration-300 group-hover:translate-x-0.5"
                        style={{ backgroundColor: `${colors.primary}10`, color: colors.ctaText }}
                      >
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )
        ) : (
          publicaciones.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm p-12 text-center border border-gray-200">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: `${colors.primary}15` }}>
                <svg className="h-8 w-8 flex-shrink-0" fill="none" stroke={colors.primary} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No hay publicaciones disponibles</h3>
              <p className="text-gray-500">Aún no hay publicaciones en esta categoría</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-5">
              {publicaciones.map((publicacion) => (
                <Link
                  key={publicacion.id}
                  to={`/${categoryId}/${publicacion.slug}`}
                  className="group flex flex-col bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 overflow-hidden border border-gray-100 hover:border-gray-300 hover:-translate-y-1"
                >
                  <div
                    className="relative w-full overflow-hidden flex-shrink-0"
                    style={{
                      aspectRatio: '3 / 4',
                      background: `linear-gradient(135deg, ${colors.cardGradientFrom}, ${colors.cardGradientVia}, ${colors.cardGradientTo})`
                    }}
                  >
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg
                        className="h-10 w-10 text-white/50 group-hover:text-white/70 transition-colors duration-200"
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                    <PdfCoverThumbnail pdfUrl={publicacion.rutaArchivo} />
                  </div>
                  <div className="p-2.5 flex flex-col gap-0.5">
                    <p className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug">
                      {publicacion.descripcion}
                    </p>
                    <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">
                      {publicacion.titulo}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )
        )}
      </main>
    </div>
  );
};

export default CategoryPage;
